import {
  type Action,
  type ActionCreate,
  type ActionInput,
  type ActionMode,
  type ActionSpec,
  type ActionTemplateSpec,
  INPUT_TYPES,
  type InputSource,
  type InputType,
  type RunnableAction,
  actionCreateSchema,
  actionSpecSchema,
  replyWithSchema,
} from "@mailhand/core/schemas";
import { z } from "zod";

export type RunnableType = RunnableAction["type"];

/** Why an input row cannot be renamed or retyped. */
export type InputLock = "reply-text" | "forward-to" | "forward-summary" | null;

export interface InputDraft {
  key: string;
  name: string;
  type: InputType;
  required: boolean;
  source: InputSource;
  value: string;
  description: string;
  lock: InputLock;
}

/** The form's working copy of a reply, forward, or webhook action. Numbers stay text until save. */
export interface ActionDraft {
  type: RunnableType;
  name: string;
  description: string;
  enabled: boolean;
  followUp: boolean;
  mode: ActionMode;
  dailyCap: string;
  inputs: InputDraft[];
  webhookUrl: string;
  timeoutSeconds: string;
  replyWith: string;
  webhookSecret: string;
}

export type FieldErrors = Record<string, string>;

export type DraftResult<Body> = { ok: true; body: Body } | { ok: false; errors: FieldErrors };

export const DEFAULT_DAILY_CAP = 100;
export const DEFAULT_TIMEOUT_SECONDS = 10;
export const SECRET_MIN_LENGTH = 16;

let keySeed = 0;
export function newInputKey(): string {
  keySeed += 1;
  return `input-${keySeed}`;
}

export function lockFor(type: RunnableType, name: string): InputLock {
  if (type === "reply" && name === "text") return "reply-text";
  if (type === "forward" && name === "to") return "forward-to";
  if (type === "forward" && name === "summary") return "forward-summary";
  return null;
}

export function blankInput(): InputDraft {
  return { key: newInputKey(), name: "", type: "string", required: true, source: "extract", value: "", description: "", lock: null };
}

function replyTextInput(): InputDraft {
  return { key: newInputKey(), name: "text", type: "text", required: true, source: "compose", value: "", description: "", lock: "reply-text" };
}

export function forwardToInput(address: string): InputDraft {
  return { key: newInputKey(), name: "to", type: "email", required: true, source: "constant", value: address, description: "", lock: "forward-to" };
}

export function summaryInput(): InputDraft {
  return { key: newInputKey(), name: "summary", type: "text", required: false, source: "compose", value: "", description: "One line above the forwarded message.", lock: "forward-summary" };
}

export function emptyDraft(type: RunnableType): ActionDraft {
  const inputs = type === "reply" ? [replyTextInput()] : type === "forward" ? [forwardToInput("")] : [];
  return {
    type,
    name: "",
    description: "",
    enabled: true,
    followUp: false,
    mode: type === "forward" ? "auto" : "approve",
    dailyCap: String(DEFAULT_DAILY_CAP),
    inputs,
    webhookUrl: "",
    timeoutSeconds: String(DEFAULT_TIMEOUT_SECONDS),
    replyWith: "none",
    webhookSecret: "",
  };
}

function inputToDraft(type: RunnableType, input: ActionInput): InputDraft {
  return {
    key: newInputKey(),
    name: input.name,
    type: input.type,
    required: input.required,
    source: input.source,
    value: input.source === "constant" || input.source === "template" ? input.value : "",
    description: input.description ?? "",
    lock: lockFor(type, input.name),
  };
}

export function draftFromAction(action: RunnableAction): ActionDraft {
  return {
    type: action.type,
    name: action.name,
    description: action.description,
    enabled: action.enabled,
    followUp: action.followUp,
    mode: action.mode,
    dailyCap: String(action.dailyCap),
    inputs: action.inputs.map((input) => inputToDraft(action.type, input)),
    webhookUrl: action.type === "webhook" ? action.webhook.url : "",
    timeoutSeconds: action.type === "webhook" ? String(action.webhook.timeoutMs / 1000) : String(DEFAULT_TIMEOUT_SECONDS),
    replyWith: action.type === "webhook" ? action.afterRun.replyWith : "none",
    webhookSecret: "",
  };
}

/**
 * A template's spec, as `GET /actions/templates` returns it. Templates leave the
 * webhook URL for the tenant to fill ("https://"), which `actionSpecSchema`
 * rejects, so this reads the parts the form needs without the URL rule.
 */
export function draftFromTemplate(spec: ActionTemplateSpec): ActionDraft {
  const base = emptyDraft(spec.type);
  const inputs = spec.inputs.map((input) => inputToDraft(spec.type, input));
  return {
    ...base,
    name: spec.name,
    description: spec.description,
    enabled: spec.enabled,
    followUp: spec.followUp,
    mode: spec.mode,
    dailyCap: String(spec.dailyCap),
    inputs: withRequiredInputs(spec.type, inputs),
    webhookUrl: spec.webhook?.url ?? "",
    timeoutSeconds: spec.webhook === undefined ? base.timeoutSeconds : String(spec.webhook.timeoutMs / 1000),
    replyWith: spec.afterRun?.replyWith ?? "none",
  };
}

/** Puts the reply `text` or forward `to` row first, creating it when the source lacks it. */
function withRequiredInputs(type: RunnableType, inputs: InputDraft[]): InputDraft[] {
  if (type === "webhook") return inputs;
  const name = type === "reply" ? "text" : "to";
  const existing = inputs.find((input) => input.name === name);
  const required = existing ?? (type === "reply" ? replyTextInput() : forwardToInput(""));
  return [required, ...inputs.filter((input) => input !== existing)];
}

/** Choosing "Written by Hooky" makes the input long text, the only type the writer fills. */
export function withSource(input: InputDraft, source: InputSource): InputDraft {
  const needsValue = source === "constant" || source === "template";
  return { ...input, source, type: source === "compose" ? "text" : input.type, value: needsValue ? input.value : "" };
}

export function isInputType(value: string): value is InputType {
  return INPUT_TYPES.some((type) => type === value);
}

function inputFromDraft(draft: InputDraft): ActionInput {
  const description = draft.description.trim() === "" ? {} : { description: draft.description.trim() };
  const base = { name: draft.name.trim(), required: draft.required, ...description };
  switch (draft.source) {
    case "extract":
      return { ...base, type: draft.type, source: "extract" };
    case "compose":
      return { ...base, type: "text", source: "compose" };
    case "constant":
      return { ...base, type: draft.type, source: "constant", value: draft.value.trim() };
    case "template":
      return { ...base, type: draft.type, source: "template", value: draft.value.trim() };
  }
}

function toNumber(text: string): number {
  return text.trim() === "" ? Number.NaN : Number(text);
}

/** The action as a `PUT /actions/{id}` body: the stored action without its id. */
export function specOf(action: Action): ActionSpec {
  const { id: _id, ...spec } = action;
  return spec;
}

type RunnableSpec = Extract<ActionSpec, { type: RunnableType }>;

/** A spec as the form builds it. The schema checks the input rows against the exact list for the type. */
type SpecCandidate = { [Type in RunnableType]: Omit<Extract<RunnableSpec, { type: Type }>, "inputs"> & { inputs: ActionInput[] } }[RunnableType];

/** The spec object the form describes, before validation. */
function specFromDraft(draft: ActionDraft): SpecCandidate {
  const common = {
    name: draft.name.trim(),
    description: draft.description.trim(),
    enabled: draft.enabled,
    followUp: draft.followUp,
    mode: draft.mode,
    dailyCap: toNumber(draft.dailyCap),
    inputs: draft.inputs.map(inputFromDraft),
  };
  if (draft.type === "reply") return { type: "reply", ...common };
  if (draft.type === "forward") return { type: "forward", ...common };
  const replyWith = replyWithSchema.safeParse(draft.replyWith);
  return {
    type: "webhook",
    ...common,
    webhook: { url: draft.webhookUrl.trim(), timeoutMs: Math.round(toNumber(draft.timeoutSeconds) * 1000) },
    afterRun: { replyWith: replyWith.success ? replyWith.data : "none" },
  };
}

const FRIENDLY: Record<string, string> = {
  name: "Give it a name, up to 40 characters.",
  description: "Describe when to use it, up to 500 characters.",
  dailyCap: "A whole number from 1 to 10,000.",
  "webhook.url": "An https:// address, like https://api.example.com/hooks/refund.",
  "webhook.timeoutMs": "Between 1 and 25 seconds.",
  webhookSecret: `At least ${SECRET_MIN_LENGTH} characters. Your endpoint uses it to check the signature.`,
};

/** Field names follow the zod issue path: `name`, `inputs.2.value`, `webhook.url`. */
function fieldKey(path: readonly PropertyKey[]): string {
  return path.map(String).join(".");
}

function friendlyMessage(key: string, message: string): string {
  if (/^inputs\.\d+\.name$/.test(key) && !message.includes("used twice")) return "Letters, digits, and _ only, starting with a letter (up to 32).";
  if (/^inputs\.\d+\.value$/.test(key) && message.startsWith("Too small")) return "Fill in a value.";
  if (/^inputs\.\d+\.description$/.test(key)) return "Up to 200 characters.";
  return FRIENDLY[key] ?? message;
}

function collectErrors(issues: readonly z.core.$ZodIssue[], stripPrefix: string | null): FieldErrors {
  const errors: FieldErrors = {};
  for (const issue of issues) {
    const path = stripPrefix !== null && issue.path[0] === stripPrefix ? issue.path.slice(1) : issue.path;
    const key = fieldKey(path) === "" ? "form" : fieldKey(path);
    errors[key] ??= friendlyMessage(key, issue.message);
  }
  return errors;
}

export function validateForCreate(draft: ActionDraft): DraftResult<ActionCreate> {
  const secret = draft.type === "webhook" ? { webhookSecret: draft.webhookSecret } : {};
  const parsed = actionCreateSchema.safeParse({ spec: specFromDraft(draft), ...secret });
  return parsed.success ? { ok: true, body: parsed.data } : { ok: false, errors: collectErrors(parsed.error.issues, "spec") };
}

export function validateForEdit(draft: ActionDraft): DraftResult<ActionSpec> {
  const parsed = actionSpecSchema.safeParse(specFromDraft(draft));
  return parsed.success ? { ok: true, body: parsed.data } : { ok: false, errors: collectErrors(parsed.error.issues, null) };
}
